import { useEffect, useState, useRef } from "react";
import api, { formatApiError, downloadFile, previewFile } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, Upload, Download, Trash2, RotateCcw, History, FileArchive, FileText,
  FolderOpen, FolderPlus, ArrowLeft, Pencil, CalendarDays, Plus, FilePlus, Files, Eye,
} from "lucide-react";
import { toast } from "sonner";

const PLAN_CODE = "calisma_egitim_plani";
const TWO_COL = ["ek3", "sozlesmeler", "guvenlik_plani", "egitim_tatbikat"];
const ACCEPT = ".docx,.doc,.xlsx,.xls,.pdf";

function MissingBadges({ f }) {
  if (f.type === "activity") {
    if (!f.defined) return <StatusBadge tone="muted">Evrak listesi tanımlanmadı</StatusBadge>;
    const w = f.working_missing || 0, s = f.signed_missing || 0;
    if (w === 0 && s === 0) return <StatusBadge tone="active">Eksiksiz</StatusBadge>;
    return (
      <div className="flex gap-1.5 flex-wrap">
        {w > 0 && <StatusBadge tone="overdue">{w} çalışma eksik</StatusBadge>}
        {s > 0 && <StatusBadge tone="pending">{s} imzalı PDF eksik</StatusBadge>}
      </div>
    );
  }
  return <span className="text-[11px] text-slate-400">{f.file_count} dosya</span>;
}

export default function FirmDocuments({ firmId, firmTitle, canEdit }) {
  const [folders, setFolders] = useState(null);
  const [status, setStatus] = useState([]);
  const [active, setActive] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  const [trashOpen, setTrashOpen] = useState(false);
  const [trash, setTrash] = useState({ documents: [], folders: [] });
  const [versions, setVersions] = useState(null);

  const [dlg, setDlg] = useState(false);
  const [ctx, setCtx] = useState({});
  const [picked, setPicked] = useState(null);
  const [suggest, setSuggest] = useState(null);
  const [conflict, setConflict] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const bulkRef = useRef();
  const [bulkCtx, setBulkCtx] = useState(null);
  // Eğitim/Tatbikat özel toplu yükleme
  const [eduBulk, setEduBulk] = useState(null);
  const [eduFiles, setEduFiles] = useState([]);
  const [eduPersonnel, setEduPersonnel] = useState([]);
  const [eduSelPerson, setEduSelPerson] = useState("");
  const [eduTrainings, setEduTrainings] = useState([]);
  const [eduSelTraining, setEduSelTraining] = useState("");
  const [eduPublish, setEduPublish] = useState(false);
  const [eduBusy, setEduBusy] = useState(false);

  const [folderDlg, setFolderDlg] = useState(null);
  const [folderName, setFolderName] = useState("");
  const [renameDlg, setRenameDlg] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [newYear, setNewYear] = useState("");
  const [extraYears, setExtraYears] = useState([]);

  const loadGrid = async () => {
    try {
      const [fo, st] = await Promise.all([
        api.get(`/firms/${firmId}/folders-overview`),
        api.get(`/firms/${firmId}/doc-status`),
      ]);
      setFolders(fo.data.folders);
      setStatus(st.data.activities);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { loadGrid(); }, [firmId]); // eslint-disable-line

  const openFolder = async (f) => {
    setActive(f); setLoadingDocs(true); setExtraYears([]);
    try { const { data } = await api.get(`/firms/${firmId}/documents?folder=${f.code}`); setDocs(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoadingDocs(false); }
  };
  const refresh = async () => { await loadGrid(); if (active) await openFolder(active); };

  // ---- Upload ----
  const openUpload = (c) => { setCtx(c); setPicked(null); setSuggest(null); setConflict(null); if (fileRef.current) fileRef.current.value = ""; setDlg(true); };

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPicked(f); setConflict(null);
    if (ctx.activityCode && !ctx.expectedKey) {
      try {
        const { data } = await api.post(`/firms/${firmId}/match-suggest`, { activity_code: ctx.activityCode, filename: f.name });
        if (data.defined && data.suggestion && !data.suggestion.ambiguous) {
          setCtx((x) => ({ ...x, expectedKey: `${ctx.activityCode}:${data.suggestion.key}` }));
          setSuggest({ ok: true, name: data.suggestion.name });
        } else if (data.defined) setSuggest({ ambiguous: true });
      } catch {}
    }
  };

  const postFile = async (file, c, extra = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("section", c.section || "internal");
    fd.append("folder_code", c.folderCode || "genel");
    if (c.expectedKey) fd.append("expected_key", c.expectedKey);
    if (c.year) fd.append("year", String(c.year));
    if (c.section === "customer") fd.append("is_signed_final", String(c.isSigned ?? true));
    if (c.versionOf) fd.append("version_of", c.versionOf);
    if (extra.versionOf) fd.append("version_of", extra.versionOf);
    if (extra.confirmNew) fd.append("confirm_new", "true");
    return api.post(`/firms/${firmId}/documents`, fd);
  };

  const doUpload = async (extra = {}) => {
    if (!picked) { toast.error("Lütfen bir dosya seçin."); return; }
    setBusy(true);
    try {
      await postFile(picked, ctx, extra);
      toast.success("Dosya yüklendi.");
      setDlg(false); setPicked(null); setConflict(null);
      refresh();
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.detail?.code === "name_exists") {
        setConflict(e.response.data.detail);
      } else toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const bulkUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (bulkRef.current) bulkRef.current.value = "";
    if (!files.length || !bulkCtx) return;
    setBusy(true);
    let ok = 0;
    for (const f of files) {
      try { await postFile(f, bulkCtx, { confirmNew: true }); ok++; } catch {}
    }
    setBusy(false);
    toast.success(`${ok}/${files.length} dosya yüklendi.`);
    refresh();
  };
  const startBulk = (c) => { setBulkCtx(c); setTimeout(() => bulkRef.current?.click(), 0); };

  // ---- Eğitim/Tatbikat: adlı toplu yükleme (personel/eğitim bağlantılı, sertifika eşleştirmeden ayrı) ----
  const openEduBulk = async (section) => {
    setEduBulk({ section }); setEduFiles([]); setEduSelPerson(""); setEduSelTraining(""); setEduTrainings([]); setEduPublish(section === "customer");
    try { const { data } = await api.get(`/firms/${firmId}/personnel`); setEduPersonnel(data); } catch { setEduPersonnel([]); }
  };
  const onEduPerson = async (pid) => {
    setEduSelPerson(pid); setEduSelTraining(""); setEduTrainings([]);
    if (pid) { try { const { data } = await api.get(`/personnel/${pid}/trainings`); setEduTrainings(data); } catch { /* yetki/akış */ } }
  };
  const doEduBulk = async () => {
    if (!eduFiles.length) { toast.error("Dosya seçin."); return; }
    setEduBusy(true);
    let ok = 0;
    for (const f of eduFiles) {
      try {
        if (eduSelPerson) {
          const fd = new FormData();
          fd.append("file", f); fd.append("section", eduBulk.section);
          if (eduSelTraining) fd.append("training_id", eduSelTraining);
          if (eduBulk.section === "customer" && eduPublish) fd.append("publish", "true");
          await api.post(`/personnel/${eduSelPerson}/documents`, fd);
        } else {
          const fd = new FormData();
          fd.append("file", f); fd.append("folder_code", "egitim_tatbikat"); fd.append("section", eduBulk.section); fd.append("confirm_new", "true");
          if (eduBulk.section === "customer") fd.append("is_signed_final", "true");
          const res = await api.post(`/firms/${firmId}/documents`, fd);
          if (eduBulk.section === "customer" && eduPublish && res.data?.id) {
            await api.patch(`/documents/${res.data.id}`, { visibility: "customer", published: true });
          }
        }
        ok++;
      } catch { /* tek dosya hatası atlanır */ }
    }
    setEduBusy(false);
    toast.success(`${ok}/${eduFiles.length} dosya yüklendi.`);
    setEduBulk(null); refresh();
  };

  // ---- Doc actions ----
  const publish = async (d) => { try { await api.patch(`/documents/${d.id}`, { visibility: "customer", published: !d.published }); refresh(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (d) => { try { await api.post(`/documents/${d.id}/delete`); toast.success("Çöp kutusuna taşındı."); refresh(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const showVersions = async (d) => { try { const { data } = await api.get(`/documents/${d.id}/versions`); setVersions(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const openTrash = async () => { try { const { data } = await api.get(`/firms/${firmId}/trash`); setTrash(data); setTrashOpen(true); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const restore = async (d) => { try { await api.post(`/documents/${d.id}/restore`); toast.success("Geri yüklendi."); openTrash(); refresh(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const deleteFolder = async () => { try { await api.delete(`/firms/${firmId}/folders/${confirmDel.code}`); toast.success("Klasör çöp kutusuna taşındı."); const c = confirmDel.code; setConfirmDel(null); loadGrid(); if (active?.code === c) setActive(null); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const restoreFolder = async (code) => { try { await api.post(`/firms/${firmId}/folders/${code}/restore`); toast.success("Klasör geri yüklendi."); openTrash(); loadGrid(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const createFolder = async () => {
    if (!folderName.trim()) return;
    try { await api.post(`/firms/${firmId}/folders`, { name: folderName.trim(), parent_code: folderDlg.parent_code || null }); toast.success("Klasör oluşturuldu."); setFolderDlg(null); setFolderName(""); loadGrid(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const renameFolder = async () => {
    if (!folderName.trim()) return;
    try { await api.patch(`/firms/${firmId}/folders/${renameDlg.code}`, { name: folderName.trim() }); toast.success("Yeniden adlandırıldı."); setRenameDlg(null); setFolderName(""); loadGrid(); if (active?.code === renameDlg.code) setActive({ ...active, name: folderName.trim() }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (folders === null) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;

  const activityFolders = folders.filter((f) => f.type === "activity");
  const generalFolders = folders.filter((f) => f.type === "general");
  const customFolders = folders.filter((f) => f.type === "custom" && !f.parent_code);
  const childrenOf = (code) => folders.filter((f) => f.type === "custom" && f.parent_code === code);

  // ===================== GRID =====================
  if (!active) {
    const FolderCard = ({ f }) => (
      <Card className="border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => openFolder(f)} data-testid={`folder-card-${f.code}`}>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-start gap-3">
            <FolderOpen className={`h-6 w-6 shrink-0 ${f.type === "activity" ? "text-sky-500" : f.type === "custom" ? "text-violet-500" : "text-amber-500"}`} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-800 truncate">{f.name}</div>
              <div className="mt-1.5"><MissingBadges f={f} /></div>
            </div>
            {canEdit && f.type === "custom" && (
              <div className="flex gap-0.5" onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setRenameDlg(f); setFolderName(f.name); }} data-testid={`folder-rename-${f.code}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => setConfirmDel(f)} data-testid={`folder-delete-${f.code}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadFile(`/firms/${firmId}/download-zip`, `${firmTitle}.zip`)} data-testid="firm-bulk-zip"><FileArchive className="h-4 w-4 mr-1" /> Tümü ZIP</Button>
          {canEdit && <Button variant="outline" size="sm" onClick={openTrash} data-testid="firm-trash-btn"><Trash2 className="h-4 w-4 mr-1" /> Çöp Kutusu</Button>}
        </div>
        {activityFolders.length > 0 && <div>
          <h3 className="font-display text-sm font-semibold text-slate-700 mb-2">Faaliyet Klasörleri</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{activityFolders.map((f) => <FolderCard key={f.code} f={f} />)}</div>
        </div>}
        <div>
          <h3 className="font-display text-sm font-semibold text-slate-700 mb-2">Genel Klasörler</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{generalFolders.map((f) => <FolderCard key={f.code} f={f} />)}</div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display text-sm font-semibold text-slate-700">Özel Klasörler <span className="font-normal text-xs text-slate-400">(iç — müşteriye görünmez)</span></h3>
            {canEdit && <Button variant="outline" size="sm" onClick={() => { setFolderDlg({ parent_code: null }); setFolderName(""); }} data-testid="create-folder-btn"><FolderPlus className="h-4 w-4 mr-1" /> Yeni Klasör</Button>}
          </div>
          {customFolders.length === 0 ? <p className="text-sm text-slate-400">Özel klasör yok.</p> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{customFolders.map((f) => <FolderCard key={f.code} f={f} />)}</div>
          )}
        </div>
        {NewFolderDialog()}
        {RenameDialog()}
        {TrashDialog()}
        {ConfirmDeleteDialog()}
        {bulkInput()}
      </div>
    );
  }

  // ===================== FOLDER DETAIL =====================
  const isActivity = active.type === "activity";
  const isPlan = active.code === PLAN_CODE;
  const isTwoCol = TWO_COL.includes(active.code);
  const actStatus = isActivity ? status.find((a) => a.activity_code === active.activity_code) : null;
  const expectedKeys = (actStatus?.docs || []).map((d) => `${active.activity_code}:${d.key}`);
  const extraDocs = docs.filter((d) => !d.expected_key || !expectedKeys.includes(d.expected_key));
  const internalDocs = docs.filter((d) => d.visibility === "internal");
  const customerDocs = docs.filter((d) => d.visibility === "customer");

  const DocRow = ({ d, baseCtx, showPublish }) => (
    <div className="flex items-center justify-between gap-2 py-1.5" data-testid={`doc-row-${d.id}`}>
      <div className="flex items-center gap-2 min-w-0">
        <FileText className="h-4 w-4 text-slate-400 shrink-0" />
        <span className="text-sm text-slate-800 truncate">{d.name}.{d.ext} <span className="text-xs text-slate-400">v{d.version}</span></span>
        {showPublish && (d.published ? <StatusBadge tone="active">Yayımda</StatusBadge> : <StatusBadge tone="muted">Taslak</StatusBadge>)}
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        {d.ext === "pdf" && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => previewFile(`/documents/${d.id}/download`)} data-testid={`doc-preview-${d.id}`} title="Önizle"><Eye className="h-4 w-4" /></Button>}
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => downloadFile(`/documents/${d.id}/download`, `${d.name}.${d.ext}`)} data-testid={`doc-download-${d.id}`} title="İndir"><Download className="h-4 w-4" /></Button>
        {canEdit && <>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openUpload({ ...baseCtx, versionOf: d.id })} data-testid={`doc-newversion-${d.id}`} title="Yeni sürüm"><Upload className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => showVersions(d)} data-testid={`doc-versions-${d.id}`} title="Sürümler"><History className="h-4 w-4" /></Button>
          {showPublish && <Button variant="ghost" size="sm" className={`h-7 px-2 text-xs ${d.published ? "text-emerald-600" : "text-sky-600"}`} onClick={() => publish(d)} data-testid={`doc-publish-${d.id}`}>{d.published ? "Geri al" : "Yayımla"}</Button>}
          <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => del(d)} data-testid={`doc-delete-${d.id}`} title="Sil"><Trash2 className="h-4 w-4" /></Button>
        </>}
      </div>
    </div>
  );

  const FileColumn = ({ title, icon, list, baseCtx, showPublish, hint, eduBulk: eduBulkCol }) => (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`col-${baseCtx.section}-${baseCtx.year || active.code}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">{icon}{title}</h4>
        {canEdit && (
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => openUpload({ ...baseCtx })} data-testid={`add-file-${baseCtx.section}-${baseCtx.year || active.code}`}><FilePlus className="h-3.5 w-3.5 mr-1" /> Dosya ekle</Button>
            {eduBulkCol ? (
              <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => openEduBulk(baseCtx.section)} data-testid={`edu-bulk-${baseCtx.section}`}><Files className="h-3.5 w-3.5 mr-1" /> Toplu Dosya Yükle</Button>
            ) : (
              <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => startBulk(baseCtx)} data-testid={`bulk-${baseCtx.section}-${baseCtx.year || active.code}`}><Files className="h-3.5 w-3.5 mr-1" /> Toplu</Button>
            )}
          </div>
        )}
      </div>
      {hint && <p className="text-[11px] text-slate-400 mb-1">{hint}</p>}
      {list.length === 0 ? <p className="text-sm text-slate-400 py-2">Dosya yok.</p> : (
        <div className="divide-y divide-slate-100">{list.map((d) => <DocRow key={d.id} d={d} baseCtx={baseCtx} showPublish={showPublish} />)}</div>
      )}
    </div>
  );

  // yıllık plan yılları
  const planYears = (() => {
    const ys = new Set(extraYears);
    docs.forEach((d) => { if (d.year) ys.add(d.year); });
    return Array.from(ys).sort((a, b) => b - a);
  })();

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => setActive(null)} data-testid="folder-back"><ArrowLeft className="h-4 w-4 mr-1" /> Klasörler</Button>
        <div className="flex items-center gap-2">
          <span className="font-display font-semibold text-slate-800">{active.name}</span>
          <Button variant="outline" size="sm" onClick={() => downloadFile(`/firms/${firmId}/download-zip?folder=${active.code}`, `${active.name}.zip`)} data-testid="folder-zip"><FileArchive className="h-4 w-4 mr-1" /> ZIP</Button>
        </div>
      </div>

      {loadingDocs ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div> : (
        <>
          {/* Faaliyet klasörü: beklenen evrak satırları (yan yana slotlar) */}
          {isActivity && (
            !actStatus?.defined ? (
              <Card className="border-amber-200 bg-amber-50"><CardContent className="py-4 text-sm text-amber-700" data-testid="activity-undefined">Bu faaliyet için evrak listesi tanımlanmadı. Evrak Kataloğu ekranından tanımlayın.</CardContent></Card>
            ) : (
              <div className="space-y-3">
                {actStatus.docs.map((d) => {
                  const ek = `${active.activity_code}:${d.key}`;
                  return (
                    <Card key={d.key} className="border-slate-200" data-testid={`expected-row-${d.key}`}>
                      <CardContent className="py-3 space-y-2">
                        <div className="font-medium text-sm text-slate-800">{d.name}</div>
                        <div className="grid md:grid-cols-2 gap-3">
                          <Slot label="Çalışma dosyası" doc={d.working_doc} present={d.working === "Var"} baseCtx={{ folderCode: active.code, section: "internal", expectedKey: ek }} />
                          <Slot label="İmzalı müşteri PDF'si" doc={d.signed_doc} present={d.signed_pdf === "Var"} showPublish baseCtx={{ folderCode: active.code, section: "customer", expectedKey: ek, isSigned: true }} />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                {FileColumn({ title: "Diğer / ek dosyalar", icon: <FolderOpen className="h-4 w-4 text-slate-400" />, list: extraDocs, baseCtx: { folderCode: active.code, section: "internal", activityCode: active.activity_code, showSection: true }, showPublish: false, hint: "Katalog dışı ek dosyalar (zorunlu evrak sayılmaz)." })}
              </div>
            )
          )}

          {/* Yıllık plan: yıl grupları, her yıl iki sütun */}
          {isPlan && (
            <div className="space-y-3">
              {canEdit && (
                <div className="flex items-end gap-2">
                  <div className="space-y-1"><Label className="text-xs">Yıl ekle</Label>
                    <Input type="number" value={newYear} onChange={(e) => setNewYear(e.target.value)} placeholder="2026" className="w-32" data-testid="plan-year-input" /></div>
                  <Button variant="outline" size="sm" onClick={() => { const y = parseInt(newYear, 10); if (y) { setExtraYears((x) => [...new Set([...x, y])]); setNewYear(""); } }} data-testid="plan-year-add"><Plus className="h-4 w-4 mr-1" /> Ekle</Button>
                </div>
              )}
              {planYears.length === 0 ? <p className="text-sm text-slate-500 py-2">Henüz yıl eklenmedi.</p> : planYears.map((y) => (
                <Card key={y} className="border-slate-200" data-testid={`plan-year-${y}`}>
                  <CardContent className="py-3 space-y-2">
                    <div className="font-medium text-sm text-slate-800 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-sky-500" /> {y} Yılı</div>
                    <div className="grid md:grid-cols-2 gap-3">
                      {FileColumn({ title: "İç Çalışma Dosyaları", icon: <FileText className="h-4 w-4 text-sky-500" />, list: docs.filter((d) => d.year === y && d.visibility === "internal"), baseCtx: { folderCode: active.code, section: "internal", year: y }, showPublish: false })}
                      {FileColumn({ title: "Müşteri PDF'leri", icon: <FileText className="h-4 w-4 text-emerald-500" />, list: docs.filter((d) => d.year === y && d.visibility === "customer"), baseCtx: { folderCode: active.code, section: "customer", year: y, isSigned: true }, showPublish: true })}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* İki sütunlu genel klasörler */}
          {isTwoCol && (
            <div className="grid md:grid-cols-2 gap-4">
              {FileColumn({ title: "İç Çalışma Dosyaları", icon: <FileText className="h-4 w-4 text-sky-500" />, list: internalDocs, baseCtx: { folderCode: active.code, section: "internal" }, showPublish: false, hint: "Word/Excel/PDF — yalnız yönetici ve atanmış danışman.", eduBulk: active.code === "egitim_tatbikat" })}
              {FileColumn({ title: "Müşteri PDF'leri", icon: <FileText className="h-4 w-4 text-emerald-500" />, list: customerDocs, baseCtx: { folderCode: active.code, section: "customer", isSigned: true }, showPublish: true, hint: "İmzalı/taratılmış PDF — yayımlananı müşteri görür.", eduBulk: active.code === "egitim_tatbikat" })}
            </div>
          )}

          {/* Diğer genel + özel klasörler: tek liste */}
          {!isActivity && !isPlan && !isTwoCol && (
            FileColumn({ title: active.type === "custom" ? "Dosyalar (iç)" : "Dosyalar", icon: <FolderOpen className="h-4 w-4 text-slate-400" />, list: active.type === "custom" ? internalDocs : docs, baseCtx: { folderCode: active.code, section: "internal", showSection: active.type !== "custom" }, showPublish: active.type !== "custom" })
          )}

          {/* Özel klasör alt klasörleri */}
          {active.type === "custom" && (
            <div>
              {canEdit && <Button variant="outline" size="sm" className="mb-2" onClick={() => { setFolderDlg({ parent_code: active.code }); setFolderName(""); }} data-testid="create-subfolder-btn"><FolderPlus className="h-4 w-4 mr-1" /> Alt Klasör</Button>}
              {childrenOf(active.code).length > 0 && (
                <div className="grid sm:grid-cols-2 gap-2">
                  {childrenOf(active.code).map((c) => (
                    <div key={c.code} className="flex items-center gap-2 p-2 rounded border border-slate-200 hover:bg-slate-50 cursor-pointer" onClick={() => openFolder(c)} data-testid={`subfolder-${c.code}`}>
                      <FolderOpen className="h-4 w-4 text-violet-500" /><span className="text-sm">{c.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {UploadDialog()}
      {VersionsDialog()}
      {NewFolderDialog()}
      {RenameDialog()}
      {TrashDialog()}
      {ConfirmDeleteDialog()}
      {EduBulkDialog()}
      {bulkInput()}
    </div>
  );

  // ===================== Parçalar =====================
  function Slot({ label, doc, present, baseCtx, showPublish }) {
    return (
      <div className="rounded-md bg-slate-50 p-2.5 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge tone={present ? "active" : (showPublish ? "pending" : "overdue")}>{label}: {present ? "Var" : "Eksik"}</StatusBadge>
          {doc && showPublish && (doc.published ? <StatusBadge tone="active">Yayımda</StatusBadge> : <StatusBadge tone="muted">Taslak</StatusBadge>)}
        </div>
        {doc && <div className="flex items-center justify-between gap-1">
          <span className="text-xs text-slate-500 truncate">{doc.name}.{doc.ext} v{doc.version}</span>
          <div className="flex items-center gap-0.5 shrink-0">
            {doc.ext === "pdf" && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => previewFile(`/documents/${doc.id}/download`)} data-testid={`slot-preview-${doc.id}`} title="Önizle"><Eye className="h-4 w-4" /></Button>}
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => downloadFile(`/documents/${doc.id}/download`, `${doc.name}.${doc.ext}`)} data-testid={`slot-download-${doc.id}`}><Download className="h-4 w-4" /></Button>
            {canEdit && <>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => showVersions(doc)} title="Sürümler"><History className="h-4 w-4" /></Button>
              {showPublish && <Button variant="ghost" size="sm" className={`h-7 px-2 text-xs ${doc.published ? "text-emerald-600" : "text-sky-600"}`} onClick={() => publish(doc)} data-testid={`slot-publish-${doc.id}`}>{doc.published ? "Geri al" : "Yayımla"}</Button>}
              <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => del(doc)} title="Sil"><Trash2 className="h-4 w-4" /></Button>
            </>}
          </div>
        </div>}
        {canEdit && <Button variant="outline" size="sm" className="w-full h-7 text-xs" onClick={() => openUpload(doc ? { ...baseCtx, versionOf: doc.id } : baseCtx)} data-testid={`slot-upload-${baseCtx.section}-${baseCtx.expectedKey}`}><Upload className="h-3.5 w-3.5 mr-1" /> {doc ? "Yeni sürüm" : "Yükle"}</Button>}
      </div>
    );
  }

  function bulkInput() {
    return <input ref={bulkRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={bulkUpload} data-testid="bulk-file-input" />;
  }

  function EduBulkDialog() {
    const section = eduBulk?.section;
    const accept = section === "customer" ? ".pdf" : ACCEPT;
    return (
      <Dialog open={!!eduBulk} onOpenChange={(o) => !o && setEduBulk(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Toplu Dosya Yükle — {section === "customer" ? "Müşteri PDF'leri" : "İç Çalışma Dosyaları"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Tutanak, çizelge, katılım formu ve test belgelerini topluca yükleyin. (Dosya adından personel eşleştiren "Toplu Sertifika Yükle" akışından ayrıdır.)</p>
            <div className="space-y-1.5">
              <Label>{section === "customer" ? "PDF dosyaları" : "Word / Excel / PDF dosyaları"}</Label>
              <Input type="file" accept={accept} multiple onChange={(e) => setEduFiles(Array.from(e.target.files || []))} data-testid="edu-bulk-files" />
              <span className="text-xs text-slate-500">{eduFiles.length} dosya seçildi</span>
            </div>
            <div className="space-y-1.5">
              <Label>Personel bağlantısı (opsiyonel)</Label>
              <Select value={eduSelPerson || "none"} onValueChange={(v) => onEduPerson(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="edu-bulk-person"><SelectValue placeholder="Firma geneli (bağlantısız)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Firma geneli (bağlantısız)</SelectItem>
                  {eduPersonnel.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} {p.surname} — {p.duty}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {eduSelPerson && (
              <div className="space-y-1.5">
                <Label>Eğitim kaydı bağlantısı (opsiyonel)</Label>
                <Select value={eduSelTraining || "none"} onValueChange={(v) => setEduSelTraining(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="edu-bulk-training"><SelectValue placeholder="Seçilmedi" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seçilmedi</SelectItem>
                    {eduTrainings.map((t) => <SelectItem key={t.id} value={t.id}>{t.training_date || "Tarih girilmedi"}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {section === "customer" && (
              <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={eduPublish} onChange={(e) => setEduPublish(e.target.checked)} data-testid="edu-bulk-publish" /> Yükledikten sonra müşteriye yayımla</label>
            )}
            <p className="text-xs text-slate-400">Dosyalar mevcut "Eğitim/Tatbikat Belgeleri" klasörüne yüklenir; yeni klasör veya ikinci kopya oluşturulmaz. Dosya işlemleri müşteriye e-posta göndermez.</p>
          </div>
          <DialogFooter>
            <Button className="bg-sky-600 hover:bg-sky-700" onClick={doEduBulk} disabled={eduBusy || !eduFiles.length} data-testid="edu-bulk-confirm">{eduBusy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Yükle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  function UploadDialog() {
    return (
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{ctx.versionOf ? "Yeni Sürüm Yükle" : "Dosya Ekle"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Dosya</Label>
              <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={onPick} data-testid="doc-file-input" />
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="pick-file-btn"><Upload className="h-4 w-4 mr-1" /> Dosya Seç</Button>
                <span className="text-sm text-slate-600 truncate" data-testid="picked-filename">{picked ? picked.name : "Henüz dosya seçilmedi"}</span>
              </div>
            </div>

            {ctx.showSection && !ctx.versionOf && (
              <div className="space-y-1.5"><Label>Bölüm</Label>
                <Select value={ctx.section} onValueChange={(v) => setCtx({ ...ctx, section: v })}>
                  <SelectTrigger data-testid="upload-section"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">İç Çalışma Dosyası (müşteri görmez)</SelectItem>
                    <SelectItem value="customer">Müşteri PDF'i</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {ctx.showSection && ctx.activityCode && !ctx.versionOf && (
              <div className="space-y-1.5"><Label>Beklenen Evrak</Label>
                <Select value={ctx.expectedKey || ""} onValueChange={(v) => setCtx({ ...ctx, expectedKey: v })}>
                  <SelectTrigger data-testid="upload-expected"><SelectValue placeholder="Evrak seçin (opsiyonel)" /></SelectTrigger>
                  <SelectContent>
                    {(status.find((a) => a.activity_code === ctx.activityCode)?.docs || []).map((d) => (
                      <SelectItem key={d.key} value={`${ctx.activityCode}:${d.key}`}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {suggest?.ok && <p className="text-xs text-emerald-600">Önerilen eşleşme: {suggest.name}</p>}
                {suggest?.ambiguous && <p className="text-xs text-amber-600">Belirsiz eşleşme — lütfen evrak seçin.</p>}
              </div>
            )}

            {conflict && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="conflict-box">
                <p className="text-sm text-amber-800">{conflict.message}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => doUpload({ versionOf: conflict.existing_id })} disabled={busy} data-testid="conflict-version">Yeni sürüm yap</Button>
                  <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={() => doUpload({ confirmNew: true })} disabled={busy} data-testid="conflict-new">Yeni belge ekle</Button>
                </div>
              </div>
            )}

            <p className="text-xs text-slate-500">Belge içeriği değiştirilmez; yüklediğiniz dosya olduğu gibi saklanır. Dosya işlemleri müşteriye e-posta göndermez.</p>
          </div>
          <DialogFooter>
            {!conflict && <Button className="bg-sky-600 hover:bg-sky-700" onClick={() => doUpload()} disabled={busy || !picked} data-testid="upload-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} {ctx.versionOf ? "Yeni Sürüm Yükle" : "Yükle"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  function VersionsDialog() {
    return (
      <Dialog open={!!versions} onOpenChange={(o) => !o && setVersions(null)}>
        <DialogContent><DialogHeader><DialogTitle className="font-display">Sürüm Geçmişi</DialogTitle></DialogHeader>
          <div className="divide-y divide-slate-100">
            {(versions || []).map((v) => (
              <div key={v.id} className="flex items-center justify-between py-2 text-sm">
                <span>v{v.version} {v.is_latest && <StatusBadge tone="active">Güncel</StatusBadge>} {v.deleted && <StatusBadge tone="passive">Silinmiş</StatusBadge>}<span className="text-xs text-slate-400 ml-2">{formatDateTime(v.created_at)}</span></span>
                <Button variant="ghost" size="sm" onClick={() => downloadFile(`/documents/${v.id}/download`, `${v.name}_v${v.version}.${v.ext}`)}><Download className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  function NewFolderDialog() {
    return (
      <Dialog open={!!folderDlg} onOpenChange={(o) => !o && setFolderDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{folderDlg?.parent_code ? "Alt Klasör Oluştur" : "Yeni Klasör"}</DialogTitle></DialogHeader>
          <div className="space-y-1.5"><Label>Klasör adı</Label>
            <Input value={folderName} onChange={(e) => setFolderName(e.target.value)} placeholder="Örn. Ek Belgeler" data-testid="folder-name-input" /></div>
          <DialogFooter><Button className="bg-sky-600 hover:bg-sky-700" onClick={createFolder} data-testid="folder-create-confirm">Oluştur</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  function RenameDialog() {
    return (
      <Dialog open={!!renameDlg} onOpenChange={(o) => !o && setRenameDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Klasörü Yeniden Adlandır</DialogTitle></DialogHeader>
          <div className="space-y-1.5"><Label>Yeni ad</Label>
            <Input value={folderName} onChange={(e) => setFolderName(e.target.value)} data-testid="folder-rename-input" /></div>
          <DialogFooter><Button className="bg-sky-600 hover:bg-sky-700" onClick={renameFolder} data-testid="folder-rename-confirm">Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  function TrashDialog() {
    const tdocs = trash.documents || [];
    const tfolders = trash.folders || [];
    return (
      <Dialog open={trashOpen} onOpenChange={setTrashOpen}>
        <DialogContent><DialogHeader><DialogTitle className="font-display">Çöp Kutusu (30 gün)</DialogTitle></DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-4">
            {tfolders.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-slate-500 mb-1">Özel Klasörler</h4>
                <div className="divide-y divide-slate-100">
                  {tfolders.map((f) => (
                    <div key={f.code} className="flex items-center justify-between py-2" data-testid={`trash-folder-${f.code}`}>
                      <span className="text-sm flex items-center gap-1.5"><FolderOpen className="h-4 w-4 text-violet-500" /> {f.name}<span className="text-xs text-slate-400 ml-1">{f.days_remaining} gün</span></span>
                      <Button variant="outline" size="sm" onClick={() => restoreFolder(f.code)} data-testid={`trash-folder-restore-${f.code}`}><RotateCcw className="h-4 w-4 mr-1" /> Geri Yükle</Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              {tfolders.length > 0 && <h4 className="text-xs font-semibold text-slate-500 mb-1">Dosyalar</h4>}
              {tdocs.length === 0 ? <p className="text-sm text-slate-500 py-2">Silinmiş dosya yok.</p> : (
                <div className="divide-y divide-slate-100">
                  {tdocs.map((d) => (
                    <div key={d.id} className="flex items-center justify-between py-2" data-testid={`trash-row-${d.id}`}>
                      <span className="text-sm">{d.name}.{d.ext}<span className="text-xs text-slate-400 ml-2">{d.days_remaining} gün</span></span>
                      <Button variant="outline" size="sm" onClick={() => restore(d)} data-testid={`trash-restore-${d.id}`}><RotateCcw className="h-4 w-4 mr-1" /> Geri Yükle</Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {tfolders.length === 0 && tdocs.length === 0 && <p className="text-sm text-slate-500 py-2">Boş.</p>}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  function ConfirmDeleteDialog() {
    return (
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Klasörü Sil</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600"><span className="font-medium">{confirmDel?.name}</span> özel klasörünü silmek istediğinize emin misiniz? Klasör çöp kutusuna taşınır ve 30 gün içinde geri alınabilir. İçinde dosya veya alt klasör varsa silinemez.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)} data-testid="confirm-delete-cancel">Vazgeç</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={deleteFolder} data-testid="confirm-delete-ok">Sil</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
}
